import { useState, useEffect, useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { Search, Music, SlidersHorizontal, Plus, ListMusic, Trash2, AlertTriangle, RefreshCw, FolderOpen, ChevronRight, Circle, Filter, PanelRightOpen, X, Play, Pause, Volume2, VolumeX, MinusCircle, Shuffle } from 'lucide-react';
import { toast } from 'sonner';
import { API, useAppContext } from '@/App';
import { loadGenres } from '@/components/GenreEditor';
import CamelotWheel from '@/components/CamelotWheel';
import AudioPlayer from '@/components/AudioPlayer';
import GenreEditor from '@/components/GenreEditor';
import WaveformBars from '@/components/WaveformBars';
import TrackCoverButton from '@/components/TrackCoverButton';
import { Input } from '@/components/ui/input';
import { smartShuffle } from '@/utils/smartShuffle';
import useRestorePlayer from '@/utils/useRestorePlayer';
import { usePlayer } from '@/context/PlayerContext';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { AreaChart, Area, ResponsiveContainer } from 'recharts';
import { useAuth } from '@/context/AuthContext';

// Mini energy wave component for track rows
function MiniEnergyWave({ structure, height = 24, trackId = 'default' }) {
  const gradId = `miniWaveLib-${String(trackId).slice(-8)}`;
  const data = useMemo(() => {
    if (!structure || structure.length === 0) {
      return Array.from({ length: 8 }, (_, i) => ({ energy: 35 + ((i * 7 + 13) % 30) }));
    }
    return structure.map((seg) => ({
      energy: seg.energy || 50,
    }));
  }, [structure]);

  return (
    <ResponsiveContainer width="100%" height={height}>
      <AreaChart data={data} margin={{ top: 2, right: 0, left: 0, bottom: 2 }}>
        <defs>
          <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
            <stop offset="5%" stopColor="#2563FF" stopOpacity={0.8} />
            <stop offset="95%" stopColor="#2563FF" stopOpacity={0.2} />
          </linearGradient>
        </defs>
        <Area 
          type="monotone" 
          dataKey="energy" 
          stroke="#2563FF" 
          strokeWidth={1.5} 
          fill={`url(#${gradId})`}
          isAnimationActive={false}
        />
      </AreaChart>
    </ResponsiveContainer>
  );
}

export default function LibraryPage() {
  const { refreshCounts } = useAppContext();
  const { token } = useAuth();
  const [searchParams] = useSearchParams();
  const sourceFilter = searchParams.get('source'); // 'web' → show only web-uploaded tracks

  const [tracks, setTracks] = useState([]);
  const [genres, setGenres] = useState([]);
  const [playlists, setPlaylists] = useState([]);
  const [crates, setCrates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedGenre, setSelectedGenre] = useState('all');
  const [selectedCamelot, setSelectedCamelot] = useState('all');
  const [selectedCrate, setSelectedCrate] = useState('all');
  const [sortBy, setSortBy] = useState('newest');
  const [selectedTracks, setSelectedTracks] = useState([]);
  const [newPlaylistName, setNewPlaylistName] = useState('');
  const [showPlaylistDialog, setShowPlaylistDialog] = useState(false);
  const [showDeleteAllDialog, setShowDeleteAllDialog] = useState(false);
  const [dragOverCrate, setDragOverCrate] = useState(null);
  const [dragOverPlaylist, setDragOverPlaylist] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [showCamelotWheel, setShowCamelotWheel] = useState(false);
  const [showMobileSidebar, setShowMobileSidebar] = useState(false);
  const [showAddToCrateDialog, setShowAddToCrateDialog] = useState(false);
  const [viewMode, setViewMode] = useState('list'); // 'list' or 'grid'
  const [reanalyzingTracks, setReanalyzingTracks] = useState(new Set());
  const [reanalyzeAllProgress, setReanalyzeAllProgress] = useState({ active: false, current: 0, total: 0 });
  const [showReanalyzeAllDialog, setShowReanalyzeAllDialog] = useState(false);
  
  // Audio playback state — owned by global PlayerContext now so the
  // <audio> element survives navigation and iOS lock-screen suspensions.
  const { playingTrack, isPlaying, playTrack, closePlayer } = usePlayer();

  // No-op kept for older callsites
  useRestorePlayer(tracks, playingTrack, () => {});

  const handlePlayPause = (track) => {
    if (!track.has_audio && !track.storage_path) {
      toast.info('No audio available for this track yet. Upload the audio file on the Upload page.');
      return;
    }
    const playableQueue = filteredTracks.filter(t => t.has_audio || t.storage_path);
    playTrack(track, playableQueue);
  };

  const handleClosePlayer = () => {
    closePlayer();
  };

  // Calculate track counts per Camelot code
  const camelotTrackCounts = useMemo(() => {
    const counts = {};
    tracks.forEach(track => {
      const code = track.analysis?.camelot;
      if (code) {
        counts[code] = (counts[code] || 0) + 1;
      }
    });
    return counts;
  }, [tracks]);

  const fetchData = async () => {
    if (!token) return;
    
    const headers = { Authorization: `Bearer ${token}` };

    // Fire all 4 requests in parallel but let each update its own state as
    // it lands. Previously we awaited `Promise.all` — the whole sidebar sat
    // empty until the slowest response arrived (~1.6s on real libraries).
    // Now the sidebar's crates + playlists appear the moment they respond
    // even if /tracks or /genres is still in flight.
    const track = axios.get(`${API}/tracks`, { headers })
      .then(r => setTracks(r.data.tracks || []))
      .catch(e => { console.error('tracks failed:', e); throw e; });
    // Reuse GenreEditor's shared de-duplicated genre cache — the filter
    // dropdown and the per-row inline editors now share one request.
    const genres = loadGenres()
      .then(g => setGenres(g || []))
      .catch(() => setGenres([]));   // non-fatal — filter dropdown just stays empty
    const playlists = axios.get(`${API}/playlists?include_suggested=false`, { headers })
      .then(r => setPlaylists(r.data.playlists || []))
      .catch(e => { console.error('playlists failed:', e); throw e; });
    const crates = axios.get(`${API}/crates`, { headers })
      .then(r => setCrates(r.data.crates || []))
      .catch(e => { console.error('crates failed:', e); throw e; });

    try {
      // We only need to *await* completion to know when to drop the loading
      // spinner. Individual state updates already ran inline above.
      await Promise.all([track, genres, playlists, crates]);
    } catch (e) {
      console.error('Failed to fetch data:', e);
      toast.error('Failed to load library');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) {
      fetchData();
    }
  }, [token]);

  // Get track IDs in selected crate
  const selectedCrateTrackIds = selectedCrate !== 'all' 
    ? (crates.find(c => c.id === selectedCrate)?.track_ids || [])
    : null;

  const filteredTracks = tracks
    .filter(track => {
      const matchesSearch = 
        track.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        track.artist.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesGenre = selectedGenre === 'all' || track.genre === selectedGenre;
      const matchesCamelot = selectedCamelot === 'all' || track.analysis?.camelot === selectedCamelot;
      const matchesCrate = selectedCrate === 'all' || selectedCrateTrackIds?.includes(track.id);
      const matchesSource = !sourceFilter || (
        sourceFilter === 'web'
          ? track.upload_source === 'web' && !track.in_crate
          : track.upload_source === sourceFilter
      );
      return matchesSearch && matchesGenre && matchesCamelot && matchesCrate && matchesSource;
    })
    .sort((a, b) => {
      switch (sortBy) {
        case 'newest':
          return new Date(b.created_at) - new Date(a.created_at);
        case 'bpm-asc':
          return (a.analysis?.bpm || 0) - (b.analysis?.bpm || 0);
        case 'bpm-desc':
          return (b.analysis?.bpm || 0) - (a.analysis?.bpm || 0);
        case 'camelot':
          const camA = a.analysis?.camelot || 'ZZ';
          const camB = b.analysis?.camelot || 'ZZ';
          return camA.localeCompare(camB);
        case 'energy':
          return (b.analysis?.energy || 0) - (a.analysis?.energy || 0);
        case 'title':
          return a.title.localeCompare(b.title);
        case 'artist':
          return a.artist.localeCompare(b.artist);
        default:
          return 0;
      }
    });

  const toggleTrackSelection = (trackId) => {
    setSelectedTracks(prev => 
      prev.includes(trackId) 
        ? prev.filter(id => id !== trackId)
        : [...prev, trackId]
    );
  };

  const selectAllTracks = () => {
    const allIds = filteredTracks.map(t => t.id);
    if (selectedTracks.length === filteredTracks.length) {
      setSelectedTracks([]);
    } else {
      setSelectedTracks(allIds);
    }
  };

  const deleteTrack = async (trackId) => {
    if (!token) return;
    const headers = { Authorization: `Bearer ${token}` };
    
    try {
      await axios.delete(`${API}/tracks/${trackId}`, { headers });
      toast.success('Track deleted from library');
      setTracks(prev => prev.filter(t => t.id !== trackId));
      setSelectedTracks(prev => prev.filter(id => id !== trackId));
      refreshCounts();
    } catch (e) {
      toast.error('Failed to delete track');
    }
  };

  // Remove track from crate (not delete from library)
  const removeTrackFromCrate = async (trackId, crateId) => {
    if (!token) return;
    const headers = { Authorization: `Bearer ${token}` };
    
    try {
      await axios.delete(`${API}/crates/${crateId}/tracks/${trackId}`, { headers });
      toast.success('Track removed from crate');
      // Update local crate state
      setCrates(prev => prev.map(c => 
        c.id === crateId 
          ? { ...c, track_ids: (c.track_ids || []).filter(id => id !== trackId) }
          : c
      ));
    } catch (e) {
      toast.error('Failed to remove track from crate');
    }
  };

  // Handler that decides whether to delete from library or remove from crate
  const handleTrackAction = async (trackId) => {
    if (selectedCrate !== 'all') {
      // We're viewing a crate - remove from crate, not delete
      await removeTrackFromCrate(trackId, selectedCrate);
    } else {
      // We're viewing all tracks - delete from library
      await deleteTrack(trackId);
    }
  };

  const deleteSelectedTracks = async () => {
    if (selectedTracks.length === 0 || !token) return;
    
    try {
      const res = await axios.post(`${API}/tracks/bulk-delete`, { track_ids: selectedTracks });
      const deleted = res.data?.deleted ?? selectedTracks.length;
      toast.success(`Deleted ${deleted} track${deleted === 1 ? '' : 's'}`);
      setTracks(prev => prev.filter(t => !selectedTracks.includes(t.id)));
      setSelectedTracks([]);
      refreshCounts();
    } catch (e) {
      console.error('Bulk delete failed:', e);
      toast.error('Failed to delete tracks');
      fetchData();
    }
  };

  const deleteAllTracks = async () => {
    try {
      const res = await axios.post(`${API}/tracks/bulk-delete`, {});
      const deleted = res.data?.deleted ?? 0;
      toast.success(`Deleted ${deleted} track${deleted === 1 ? '' : 's'}`);
      setTracks([]);
      setSelectedTracks([]);
      setShowDeleteAllDialog(false);
      refreshCounts();
    } catch (e) {
      console.error('Delete all failed:', e);
      toast.error('Failed to delete all tracks');
      fetchData();
    }
  };

  const handleShuffle = async () => {
    // Determine the working set: if the user has a multi-selection active,
    // shuffle ONLY those. Otherwise shuffle everything currently visible
    // (Library / active Crate / active Playlist after search filter).
    const sourceTracks = selectedTracks.length > 0
      ? filteredTracks.filter(t => selectedTracks.includes(t.id))
      : filteredTracks;

    if (sourceTracks.length < 2) {
      toast.error('Need at least 2 tracks to shuffle');
      return;
    }

    const ids = sourceTracks.map(t => t.id);
    const shuffled = await smartShuffle(ids);
    if (!shuffled || shuffled.length === 0) {
      toast.error('Shuffle failed');
      return;
    }

    // Build lookup → rebuild tracks array so `filteredTracks` (derived from
    // tracks) renders in the shuffled order. Non-source tracks keep their
    // original positions at the end.
    const trackMap = {};
    tracks.forEach(t => { trackMap[t.id] = t; });

    const shuffledSet = new Set(shuffled);
    const reorderedFirst = shuffled.map(id => trackMap[id]).filter(Boolean);
    const remaining = tracks.filter(t => !shuffledSet.has(t.id));
    setTracks([...reorderedFirst, ...remaining]);

    // Find the first shuffled track that actually has audio available —
    // start playback from there. The player automatically advances through
    // the rest of the (now shuffled) queue.
    const playableQueue = reorderedFirst.filter(t => t.has_audio || t.storage_path);
    const firstPlayable = playableQueue[0];
    if (firstPlayable) {
      playTrack(firstPlayable, playableQueue);
      toast.success(`Shuffled ${shuffled.length} tracks — now playing`);
    } else {
      toast.info(`Shuffled ${shuffled.length} tracks (none have cloud audio to play)`);
    }
  };


  const createPlaylistWithSelected = async () => {
    if (!newPlaylistName.trim()) {
      toast.error('Please enter a playlist name');
      return;
    }
    if (selectedTracks.length === 0) {
      toast.error('Please select at least one track');
      return;
    }

    try {
      await axios.post(`${API}/playlists`, {
        name: newPlaylistName,
        track_ids: selectedTracks
      });
      toast.success('Playlist created!');
      setSelectedTracks([]);
      setNewPlaylistName('');
      setShowPlaylistDialog(false);
      refreshCounts();
    } catch (e) {
      toast.error('Failed to create playlist');
    }
  };

  const addToExistingPlaylist = async (playlistId) => {
    if (selectedTracks.length === 0) {
      toast.error('Please select at least one track');
      return;
    }

    try {
      for (const trackId of selectedTracks) {
        await axios.post(`${API}/playlists/${playlistId}/tracks/${trackId}`);
      }
      toast.success('Added to playlist!');
      setSelectedTracks([]);
    } catch (e) {
      toast.error('Failed to add to playlist');
    }
  };

  const formatDuration = (seconds) => {
    if (!seconds) return '--:--';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Poll /upload/status until analysis is done (or fails / times out).
  // Returns the final status payload from the backend.
  const pollAnalysisStatus = async (trackId, headers, { intervalMs = 3000, timeoutMs = 240000 } = {}) => {
    const started = Date.now();
    while (Date.now() - started < timeoutMs) {
      const { data } = await axios.get(`${API}/upload/status/${trackId}`, { headers });
      if (data.upload_status === 'ready') return data;
      if (data.upload_status === 'failed') throw new Error('Analysis failed');
      await new Promise((r) => setTimeout(r, intervalMs));
    }
    throw new Error('Analysis timed out — it may still complete in the background');
  };

  // Run Key Analysis on a single track (async job → poll until done)
  const reanalyzeTrack = async (trackId, force = false) => {
    if (!token) return;
    const headers = { Authorization: `Bearer ${token}` };

    setReanalyzingTracks(prev => new Set(prev).add(trackId));
    try {
      // Enqueue — returns immediately with { job_id, upload_status: 'queued' }
      // Pass ?force=true to bypass the "already analysed" hard-check.
      const enqueueRes = await axios.post(
        `${API}/tracks/${trackId}/reanalyze${force ? '?force=true' : ''}`,
        {},
        { headers }
      );
      if (!enqueueRes.data.success) throw new Error('Failed to enqueue analysis');

      // Hard skip: backend refused to re-run because the track is already analysed.
      if (enqueueRes.data.skipped) {
        const reason = enqueueRes.data.reason;
        toast.info(
          reason === 'already_analyzed'
            ? 'Already analysed — no credits used'
            : (enqueueRes.data.message || 'Analysis already in progress')
        );
        return;
      }

      // Otherwise wait for the worker to finish
      const finalStatus = await pollAnalysisStatus(trackId, headers);

      // Merge the fresh analysis into local state
      setTracks(prev => prev.map(t => (
        t.id === trackId
          ? { ...t, analysis: finalStatus.analysis, upload_status: 'ready' }
          : t
      )));
      toast.success('Key analysis complete');
    } catch (e) {
      console.error('Key Analysis error:', e);
      toast.error(e?.message?.includes('timed out')
        ? 'Key Analysis is taking longer than usual — refresh to see results'
        : 'Failed to run Key Analysis');
    } finally {
      setReanalyzingTracks(prev => {
        const newSet = new Set(prev);
        newSet.delete(trackId);
        return newSet;
      });
    }
  };

  // Run Key Analysis on the currently selected tracks (sequential)
  const reanalyzeSelectedTracks = async () => {
    if (!token || selectedTracks.length === 0) return;
    const headers = { Authorization: `Bearer ${token}` };

    setReanalyzeAllProgress({ active: true, current: 0, total: selectedTracks.length });
    let successCount = 0;
    let skippedCount = 0;
    let failCount = 0;

    for (let i = 0; i < selectedTracks.length; i++) {
      const tid = selectedTracks[i];
      setReanalyzeAllProgress(prev => ({ ...prev, current: i + 1 }));
      try {
        const enqueueRes = await axios.post(`${API}/tracks/${tid}/reanalyze`, {}, { headers });
        if (!enqueueRes.data.success) { failCount++; continue; }
        // Backend returns skipped:true when track is already analysed (or in progress)
        if (enqueueRes.data.skipped) { skippedCount++; continue; }
        const finalStatus = await pollAnalysisStatus(tid, headers);
        setTracks(prev => prev.map(t => (
          t.id === tid
            ? { ...t, analysis: finalStatus.analysis, upload_status: 'ready' }
            : t
        )));
        successCount++;
      } catch (e) {
        failCount++;
      }
    }

    setReanalyzeAllProgress({ active: false, current: 0, total: 0 });
    const parts = [];
    if (successCount) parts.push(`${successCount} analyzed`);
    if (skippedCount) parts.push(`${skippedCount} skipped (already done)`);
    if (failCount)    parts.push(`${failCount} failed`);
    const msg = parts.join(' · ') || 'No tracks processed';
    if (failCount === 0) toast.success(msg);
    else                 toast.info(msg);
  };

  // Run Key Analysis on every track in the library (sequential)
  const reanalyzeAllTracks = async () => {
    if (!token) return;
    const headers = { Authorization: `Bearer ${token}` };

    setShowReanalyzeAllDialog(false);
    setReanalyzeAllProgress({ active: true, current: 0, total: tracks.length });

    let successCount = 0;
    let skippedCount = 0;
    let failCount = 0;

    for (let i = 0; i < tracks.length; i++) {
      const track = tracks[i];
      setReanalyzeAllProgress(prev => ({ ...prev, current: i + 1 }));

      try {
        const enqueueRes = await axios.post(`${API}/tracks/${track.id}/reanalyze`, {}, { headers });
        if (!enqueueRes.data.success) { failCount++; continue; }
        if (enqueueRes.data.skipped) { skippedCount++; continue; }
        const finalStatus = await pollAnalysisStatus(track.id, headers);
        setTracks(prev => prev.map(t => (
          t.id === track.id
            ? { ...t, analysis: finalStatus.analysis, upload_status: 'ready' }
            : t
        )));
        successCount++;
      } catch (e) {
        console.error(`Key Analysis failed for ${track.title}:`, e);
        failCount++;
      }
    }

    setReanalyzeAllProgress({ active: false, current: 0, total: 0 });

    const parts = [];
    if (successCount) parts.push(`${successCount} analyzed`);
    if (skippedCount) parts.push(`${skippedCount} skipped (already done)`);
    if (failCount)    parts.push(`${failCount} failed`);
    const msg = parts.join(' · ') || 'No tracks processed';
    if (failCount === 0) toast.success(msg);
    else                 toast.info(msg);
  };

  // Add selected tracks to a crate
  const addSelectedToCrate = async (crateId) => {
    if (selectedTracks.length === 0) {
      toast.error('Please select tracks first');
      return;
    }

    try {
      await axios.post(`${API}/crates/${crateId}/tracks/batch`, selectedTracks);
      toast.success(`Added ${selectedTracks.length} track(s) to crate`);
      setSelectedTracks([]);
      setShowAddToCrateDialog(false);
      fetchData(); // Refresh crates
    } catch (e) {
      toast.error('Failed to add tracks to crate');
    }
  };

  // Handle drag start for a track (drags all selected if track is selected)
  const handleDragStart = (e, trackId) => {
    // If dragged track isn't selected, select only it
    let dragIds;
    if (selectedTracks.includes(trackId) && selectedTracks.length > 1) {
      dragIds = selectedTracks;
    } else {
      dragIds = [trackId];
      setSelectedTracks([trackId]);
    }
    e.dataTransfer.setData('trackIds', JSON.stringify(dragIds));
    e.dataTransfer.effectAllowed = 'copy';
    setIsDragging(true);
    
    // Custom drag image showing count
    if (dragIds.length > 1) {
      const badge = document.createElement('div');
      badge.textContent = `${dragIds.length} tracks`;
      badge.style.cssText = 'position:absolute;top:-9999px;padding:6px 14px;background:#2563FF;color:white;font-family:monospace;font-size:12px;font-weight:bold;border:1px solid #E2E8F0;white-space:nowrap;';
      document.body.appendChild(badge);
      e.dataTransfer.setDragImage(badge, 0, 0);
      setTimeout(() => document.body.removeChild(badge), 0);
    }
  };

  // Handle drop on a crate in sidebar
  const handleDropOnCrate = async (crateId, e) => {
    e.preventDefault();
    const trackIdsJson = e.dataTransfer.getData('trackIds');
    
    if (trackIdsJson) {
      try {
        const trackIds = JSON.parse(trackIdsJson);
        if (trackIds.length === 1) {
          await axios.post(`${API}/crates/${crateId}/tracks/${trackIds[0]}`);
          toast.success('Track added to crate');
        } else {
          await axios.post(`${API}/crates/${crateId}/tracks/batch`, trackIds);
          toast.success(`${trackIds.length} tracks added to crate`);
        }
        fetchData();
      } catch (err) {
        toast.error('Failed to add tracks to crate');
      }
    }
    setDragOverCrate(null);
  };

  // Handle drop on a playlist in sidebar
  const handleDropOnPlaylist = async (playlistId, e) => {
    e.preventDefault();
    const trackIdsJson = e.dataTransfer.getData('trackIds');
    
    if (trackIdsJson) {
      try {
        const trackIds = JSON.parse(trackIdsJson);
        for (const tid of trackIds) {
          await axios.post(`${API}/playlists/${playlistId}/tracks/${tid}`);
        }
        toast.success(`${trackIds.length} track${trackIds.length > 1 ? 's' : ''} added to playlist`);
        fetchData();
      } catch (err) {
        toast.error('Failed to add tracks to playlist');
      }
    }
    setDragOverPlaylist(null);
  };

  return (
    <div className="min-h-screen py-8 px-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
          <div className="flex items-center justify-between">
            <div>
              {sourceFilter === 'web' ? (
                <>
                  <div className="flex items-center gap-2 mb-1">
                    <Link to="/library" className="font-mono text-[11px] uppercase tracking-wider text-[#94A3B8] hover:text-[#2563FF] transition-colors">Library</Link>
                    <span className="text-[#94A3B8] text-[11px]">/</span>
                    <span className="font-mono text-[11px] uppercase tracking-wider text-[#2563FF]">Web Uploads</span>
                  </div>
                  <h1 className="font-heading text-3xl sm:text-4xl font-bold mb-2 text-[#0F172A] tracking-tight">
                    Web <span className="gradient-text">Uploads</span>
                  </h1>
                  <p className="font-body text-[#64748B]">
                    {filteredTracks.length} track{filteredTracks.length !== 1 ? 's' : ''} uploaded via the web — organise them into crates below
                  </p>
                </>
              ) : (
                <>
                  <h1 data-page-header="redundant" className="font-heading text-3xl sm:text-4xl font-bold mb-2 text-[#0F172A] tracking-tight">
                    Track <span className="gradient-text">library</span>
                  </h1>
                  <p className="font-body text-[#64748B]">
                    {filteredTracks.length} tracks {selectedCrate !== 'all' ? `in ${crates.find(c => c.id === selectedCrate)?.name || 'crate'}` : 'in your library'}
                  </p>
                </>
              )}
            </div>
            
            {/* Mobile Sidebar Toggle - Always visible */}
            <button
              onClick={() => setShowMobileSidebar(true)}
              className="lg:hidden flex items-center gap-2 px-3 py-2 rounded-lg bg-[#EFF6FF] border border-[#E2E8F0] text-[#2563FF]"
              data-testid="mobile-sidebar-toggle"
            >
              <PanelRightOpen className="w-5 h-5" />
              <span className="text-sm font-heading">Collections</span>
            </button>
          </div>
          
          <div className="flex items-center gap-3 flex-wrap">
            {/* Selection actions */}
            {selectedTracks.length > 0 && (
              <>
                <span className="font-mono text-sm text-[#2563FF]">
                  {selectedTracks.length} selected
                </span>
                <Button
                  variant="outline"
                  onClick={reanalyzeSelectedTracks}
                  disabled={reanalyzeAllProgress.active}
                  className="border-[#E2E8F0] text-[#64748B] hover:text-[#3B82F6] hover:border-[#2563FF]"
                  data-testid="reanalyze-selected-btn"
                >
                  <RefreshCw className={`w-4 h-4 mr-2 ${reanalyzeAllProgress.active ? 'animate-spin' : ''}`} />
                  {reanalyzeAllProgress.active
                    ? `Analyzing ${reanalyzeAllProgress.current}/${reanalyzeAllProgress.total}`
                    : 'Key Analysis'}
                </Button>
                <Button 
                  variant="outline" 
                  onClick={deleteSelectedTracks}
                  className="border-[#E2E8F0] text-[#2563FF] hover:text-[#3B82F6] hover:border-[#2563FF]"
                  data-testid="delete-selected-btn"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete
                </Button>
                <Dialog open={showPlaylistDialog} onOpenChange={setShowPlaylistDialog}>
                  <DialogTrigger asChild>
                    <Button className="btn-primary text-sm" data-testid="create-playlist-btn">
                      <Plus className="w-4 h-4 mr-2" />
                      Create Playlist
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="bg-white border-[#E2E8F0]">
                    <DialogHeader>
                      <DialogTitle className="font-heading text-[#0F172A]">Create New Playlist</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 pt-4">
                      <Input
                        placeholder="Playlist name..."
                        value={newPlaylistName}
                        onChange={(e) => setNewPlaylistName(e.target.value)}
                        className="bg-white border-[#E2E8F0] text-[#0F172A]"
                        data-testid="playlist-name-input"
                      />
                      <p className="font-mono text-sm text-[#64748B]">
                        {selectedTracks.length} tracks will be added
                      </p>
                      <Button 
                        onClick={createPlaylistWithSelected}
                        className="w-full btn-primary"
                        data-testid="confirm-create-playlist"
                      >
                        Create Playlist
                      </Button>
                    </div>
                    {playlists.length > 0 && (
                      <div className="mt-4 pt-4 border-t border-[#E2E8F0]">
                        <p className="font-heading text-sm text-[#64748B] mb-3">Or add to existing:</p>
                        <div className="space-y-2 max-h-40 overflow-y-auto">
                          {playlists.map(pl => (
                            <button
                              key={pl.id}
                              onClick={() => addToExistingPlaylist(pl.id)}
                              className="w-full text-left p-3 rounded-lg border border-[#E2E8F0] bg-white hover:border-[#2563FF]/50 hover:bg-[#F8FAFC] text-[#0F172A] font-body text-sm transition-colors"
                            >
                              {pl.name}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </DialogContent>
                </Dialog>

                {/* Add to Crate */}
                <Dialog open={showAddToCrateDialog} onOpenChange={setShowAddToCrateDialog}>
                  <DialogTrigger asChild>
                    <Button variant="outline" className="border-[#E2E8F0] text-[#64748B] hover:text-[#0F172A]" data-testid="add-to-crate-btn">
                      <FolderOpen className="w-4 h-4 mr-2" />
                      Add to Crate
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="bg-white border-[#E2E8F0]">
                    <DialogHeader>
                      <DialogTitle className="font-heading text-[#0F172A]">Add to Crate</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-2 py-4 max-h-60 overflow-y-auto">
                      {crates.length === 0 ? (
                        <div className="text-center py-4">
                          <p className="text-[#64748B] mb-2">No crates yet</p>
                          <Link to="/crates" className="text-[#2563FF] hover:text-[#3B82F6] font-heading text-[15px]">
                            Create a crate first
                          </Link>
                        </div>
                      ) : (
                        crates.map(crate => (
                          <button
                            key={crate.id}
                            onClick={() => addSelectedToCrate(crate.id)}
                            className="w-full text-left p-3 rounded-xl border border-[#E2E8F0] bg-white hover:border-[#2563FF]/50 hover:bg-[#F8FAFC] text-[#0F172A] font-body text-sm transition-colors flex items-center gap-3"
                          >
                            <div 
                              className="w-4 h-4 rounded-md flex-shrink-0"
                              style={{ backgroundColor: crate.color }}
                            />
                            <span>{crate.name}</span>
                            <span className="ml-auto text-[#64748B] text-xs">{crate.active_track_count ?? (crate.track_ids?.length || 0)} tracks</span>
                          </button>
                        ))
                      )}
                    </div>
                  </DialogContent>
                </Dialog>

                <Button 
                  variant="outline" 
                  onClick={() => setSelectedTracks([])}
                  className="border-[#E2E8F0] text-[#64748B] hover:text-[#0F172A]"
                >
                  Clear
                </Button>
              </>
            )}
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-col md:flex-row gap-4 mb-8 p-4 bg-white border border-[#E2E8F0]">
          <div className="flex-1 flex items-center gap-2">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#64748B]" />
              <Input
                type="text"
                placeholder="Search tracks or artists..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 bg-white border-[#E2E8F0] focus:border-[#2563FF] text-[#0F172A] placeholder:text-[#94A3B8]"
                data-testid="search-input"
              />
            </div>
            <button
              onClick={handleShuffle}
              disabled={filteredTracks.length < 2}
              className="flex-shrink-0 h-10 px-3 border border-[#2563FF] bg-[#2563FF] hover:bg-[#2563FF] text-[#0F172A] transition-colors disabled:opacity-30 disabled:cursor-not-allowed flex items-center justify-center"
              title="Smart Shuffle (Key + Energy)"
              data-testid="smart-shuffle-btn"
            >
              <Shuffle className="w-4 h-4" />
            </button>
          </div>

          <div className="w-full md:w-40">
            <Select value={selectedGenre} onValueChange={setSelectedGenre}>
              <SelectTrigger className="bg-white border-[#E2E8F0] text-[#0F172A]" data-testid="genre-filter">
                <Filter className="w-4 h-4 mr-2 text-[#64748B]" />
                <SelectValue placeholder="Genre" />
              </SelectTrigger>
              <SelectContent className="bg-white border-[#E2E8F0]">
                <SelectItem value="all" className="text-[#0F172A] hover:bg-[#EFF6FF]">All Genres</SelectItem>
                {genres.map(genre => (
                  <SelectItem key={genre} value={genre} className="text-[#0F172A] hover:bg-[#EFF6FF]">
                    {genre}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="w-full md:w-40">
            <Dialog open={showCamelotWheel} onOpenChange={setShowCamelotWheel}>
              <DialogTrigger asChild>
                <Button 
                  variant="outline" 
                  className={`w-full border-[#E2E8F0] ${selectedCamelot !== 'all' ? 'text-[#2563FF] border-[#2563FF]' : 'text-[#64748B]'}`}
                  data-testid="camelot-wheel-btn"
                >
                  <Circle className="w-4 h-4 mr-2" />
                  {selectedCamelot !== 'all' ? selectedCamelot : 'Camelot Wheel'}
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-white border-[#E2E8F0] max-w-lg">
                <DialogHeader>
                  <DialogTitle className="font-heading text-[#0F172A] text-center">Camelot Wheel</DialogTitle>
                </DialogHeader>
                <div className="py-4">
                  <CamelotWheel
                    selectedKey={selectedCamelot !== 'all' ? selectedCamelot : null}
                    onSelectKey={(key) => {
                      setSelectedCamelot(key || 'all');
                      if (key) setShowCamelotWheel(false);
                    }}
                    trackCounts={camelotTrackCounts}
                    size={350}
                  />
                </div>
                {selectedCamelot !== 'all' && (
                  <Button 
                    onClick={() => { setSelectedCamelot('all'); setShowCamelotWheel(false); }}
                    variant="outline"
                    className="w-full border-[#E2E8F0] text-[#64748B]"
                  >
                    Clear Filter
                  </Button>
                )}
              </DialogContent>
            </Dialog>
          </div>

          <div className="w-full md:w-48">
            <Select value={sortBy} onValueChange={setSortBy}>
              <SelectTrigger className="bg-white border-[#E2E8F0] text-[#0F172A]" data-testid="sort-filter">
                <SlidersHorizontal className="w-4 h-4 mr-2 text-[#64748B]" />
                <SelectValue placeholder="Sort by" />
              </SelectTrigger>
              <SelectContent className="bg-white border-[#E2E8F0]">
                <SelectItem value="newest" className="text-[#0F172A] hover:bg-[#EFF6FF]">Newest</SelectItem>
                <SelectItem value="title" className="text-[#0F172A] hover:bg-[#EFF6FF]">Title A-Z</SelectItem>
                <SelectItem value="artist" className="text-[#0F172A] hover:bg-[#EFF6FF]">Artist A-Z</SelectItem>
                <SelectItem value="bpm-asc" className="text-[#0F172A] hover:bg-[#EFF6FF]">BPM: Low to High</SelectItem>
                <SelectItem value="bpm-desc" className="text-[#0F172A] hover:bg-[#EFF6FF]">BPM: High to Low</SelectItem>
                <SelectItem value="camelot" className="text-[#0F172A] hover:bg-[#EFF6FF]">Camelot Code</SelectItem>
                <SelectItem value="energy" className="text-[#0F172A] hover:bg-[#EFF6FF]">Energy Level</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Crate Filter */}
          {crates.length > 0 && (
            <div className="w-full md:w-48">
              <Select value={selectedCrate} onValueChange={setSelectedCrate}>
                <SelectTrigger className="bg-white border-[#E2E8F0] text-[#0F172A]" data-testid="crate-filter">
                  <FolderOpen className="w-4 h-4 mr-2 text-[#64748B]" />
                  <SelectValue placeholder="Crate" />
                </SelectTrigger>
                <SelectContent className="bg-white border-[#E2E8F0] max-h-60">
                  <SelectItem value="all" className="text-[#0F172A] hover:bg-[#EFF6FF]">All Tracks</SelectItem>
                  {crates.map(crate => (
                    <SelectItem key={crate.id} value={crate.id} className="text-[#0F172A] hover:bg-[#EFF6FF]">
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-md" style={{ backgroundColor: crate.color }} />
                        {crate.name}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>

        {/* Main Content — tracks list takes the full width; crates & playlists live in the sidebar */}
        <div>
          {/* Tracks Display - List View Only */}
          <div className="flex-1 min-w-0">
            {loading ? (
              <div className="space-y-2">
                {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
                  <div key={i} className="h-16 rounded-xl bg-[#F1F5F9] animate-pulse border border-[#E2E8F0]" />
                ))}
              </div>
            ) : filteredTracks.length > 0 ? (
              <div className="space-y-2">
                {/* List header - Desktop only */}
                <div className="hidden lg:grid px-4 py-2 text-xs font-mono text-[#94A3B8] uppercase tracking-widest border-b border-[#E2E8F0]"
                     style={{gridTemplateColumns: '56px 1fr 52px 72px 52px 180px 72px', gap: '12px'}}>
                  <div></div>
                  <div>Title / Artist</div>
                  <div className="text-center">BPM</div>
                  <div className="text-center">Key</div>
                  <div className="text-center">Energy</div>
                  <div className="text-center">Structure</div>
                  <div className="flex items-center justify-center">
                    <button
                      onClick={selectAllTracks}
                      className={`w-4 h-4 flex-shrink-0 rounded-full border-2 flex items-center justify-center transition-all ${
                        selectedTracks.length === filteredTracks.length && filteredTracks.length > 0
                          ? 'bg-[#2563FF] border-[#2563FF]'
                          : selectedTracks.length > 0
                            ? 'bg-[#2563FF]/50 border-[#2563FF]'
                            : 'bg-[#E2E8F0] border-[#94A3B8] hover:border-[#2563FF] hover:bg-[#DBEAFE]'
                      }`}
                      data-testid="select-all-tracks"
                      title={selectedTracks.length === filteredTracks.length ? 'Deselect all' : 'Select all'}
                    >
                      {selectedTracks.length > 0 && (
                        <svg className="w-2.5 h-2.5 text-white" fill="currentColor" viewBox="0 0 20 20">
                          {selectedTracks.length === filteredTracks.length ? (
                            <path d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"/>
                          ) : (
                            <path d="M5 11h10v2H5z"/>
                          )}
                        </svg>
                      )}
                    </button>
                  </div>
                </div>
              
              {filteredTracks.map((track, index) => (
                <motion.div
                  key={track.id}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.02 }}
                  draggable
                  onDragStart={(e) => handleDragStart(e, track.id)}
                  onDragEnd={() => { setIsDragging(false); setDragOverCrate(null); setDragOverPlaylist(null); }}
                  className={`rounded-xl border border-[#E2E8F0] hover:border-[#2563FF]/50 transition-colors cursor-grab active:cursor-grabbing ${
                    selectedTracks.includes(track.id) ? 'bg-[#F1F5F9] border-[#2563FF]' : 'bg-white'
                  } ${playingTrack?.id === track.id ? 'border-[#2563FF] bg-[#EFF6FF]' : ''}`}
                  data-testid={`list-track-${track.id}`}
                >
                  {/* Mobile Layout */}
                  <div className="lg:hidden p-3">
                    <div className="flex items-center gap-3">
                      <button
                        onClick={() => handlePlayPause(track)}
                        className={`w-10 h-10 flex-shrink-0 rounded-full flex items-center justify-center transition-all ${
                          playingTrack?.id === track.id 
                            ? 'bg-[#2563FF] text-white shadow-[0_4px_12px_rgba(37,99,255,0.35)]' 
                            : 'bg-[#EFF6FF] text-[#2563FF] hover:bg-[#2563FF] hover:text-white'
                        }`}
                      >
                        {playingTrack?.id === track.id && isPlaying ? (
                          <Pause className="w-5 h-5" />
                        ) : (
                          <Play className="w-5 h-5" />
                        )}
                      </button>
                      <div className="flex-1 min-w-0">
                        <Link to={`/track/${track.id}`}>
                          <p className="font-normal text-[#3B82F6] text-[15px] truncate">{track.title}</p>
                          <p className="font-body text-[#64748B] text-xs truncate">{track.artist}</p>
                        </Link>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <span className="font-mono text-[#2563FF] text-sm font-bold">{track.analysis?.camelot || '--'}</span>
                        <p className="font-mono text-[#64748B] text-xs">{track.analysis?.bpm?.toFixed(0) || '--'} BPM</p>
                      </div>
                      {/* Mobile selection checkbox (gradient blue/mint) */}
                      <button
                        onClick={(e) => { e.stopPropagation(); toggleTrackSelection(track.id); }}
                        className={`w-5 h-5 flex-shrink-0 rounded-md border border-[#2563FF] flex items-center justify-center transition-all bg-gradient-to-br from-[#2563FF] to-[#14E6C9] shadow-[0_2px_6px_rgba(37,99,255,0.2)] active:translate-y-[1px] ${
                          selectedTracks.includes(track.id) ? '' : 'opacity-50'
                        }`}
                        data-testid={`select-track-mobile-${track.id}`}
                        title={selectedTracks.includes(track.id) ? 'Deselect' : 'Select'}
                      >
                        {selectedTracks.includes(track.id) && (
                          <svg className="w-2.5 h-2.5 text-white" fill="currentColor" viewBox="0 0 20 20">
                            <path d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"/>
                          </svg>
                        )}
                      </button>
                    </div>
                    {/* Mobile energy wave */}
                    <div className="mt-2 h-8 rounded-md bg-[#F8FAFC] border border-[#E2E8F0] overflow-hidden relative">
                      <WaveformBars
                        structure={track.analysis?.structure}
                        duration={track.duration || 1}
                        progress={0}
                        trackId={track.id}
                      />
                    </div>
                  </div>

                  {/* Desktop Layout - proper column grid */}
                  <div className="hidden lg:grid px-4 py-2.5 items-center"
                       style={{gridTemplateColumns: '56px 1fr 52px 72px 52px 180px 72px', gap: '12px'}}>
                    {/* Cover with play */}
                    <div>
                      <TrackCoverButton
                        track={track}
                        isCurrentlyPlaying={playingTrack?.id === track.id}
                        isPlaying={isPlaying}
                        onPlayPause={handlePlayPause}
                        wrapperClassName="w-12 h-12"
                      />
                    </div>

                    {/* Title / Artist / Genre */}
                    <div className="min-w-0">
                      <Link to={`/track/${track.id}`}>
                        <p className="font-medium text-[13px] text-[#3B82F6] truncate hover:text-[#2563FF] transition-colors">
                          {track.title}
                        </p>
                        <p className="font-body text-[11px] text-[#64748B] truncate mt-0.5">{track.artist}</p>
                      </Link>
                      <div className="mt-1">
                        <GenreEditor
                          track={track}
                          onChange={(updated) => setTracks(prev => prev.map(t => t.id === updated.id ? { ...t, ...updated } : t))}
                        />
                      </div>
                    </div>

                    {/* BPM */}
                    <div className="text-center">
                      <span className="font-mono text-base text-[#0F172A]">
                        {track.analysis?.bpm?.toFixed(0) || '--'}
                      </span>
                    </div>

                    {/* Key / Camelot */}
                    <div className="text-center">
                      <span className="font-mono text-base text-[#2563FF] font-bold block">
                        {track.analysis?.camelot || '--'}
                      </span>
                      <span className="font-mono text-[10px] text-[#64748B] block mt-0.5">
                        {track.analysis?.key || '--'}
                      </span>
                    </div>

                    {/* Energy */}
                    <div className="text-center">
                      <span className="font-mono text-base text-[#64748B]">
                        {track.analysis?.energy?.toFixed(0) || '--'}
                      </span>
                    </div>

                    {/* Structure waveform */}
                    <div className="h-[42px] rounded-lg bg-white/70 border border-[#E2E8F0] overflow-hidden relative">
                      <WaveformBars
                        structure={track.analysis?.structure}
                        duration={track.duration || 1}
                        progress={0}
                        trackId={track.id}
                      />
                    </div>

                    {/* Selection */}
                    <div className="flex justify-center">
                      <button
                        onClick={() => toggleTrackSelection(track.id)}
                        className={`w-4 h-4 rounded-full border-2 flex items-center justify-center transition-all ${
                          selectedTracks.includes(track.id)
                            ? 'bg-[#2563FF] border-[#2563FF]'
                            : 'bg-[#E2E8F0] border-[#94A3B8] hover:border-[#2563FF] hover:bg-[#DBEAFE]'
                        }`}
                        data-testid={`select-track-${track.id}`}
                        title={selectedTracks.includes(track.id) ? 'Deselect' : 'Select'}
                      >
                        {selectedTracks.includes(track.id) && (
                          <svg className="w-2.5 h-2.5 text-white" fill="currentColor" viewBox="0 0 20 20">
                            <path d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"/>
                          </svg>
                        )}
                      </button>
                    </div>
                  </div>
                </motion.div>
              ))}
              </div>
            ) : (
              <div className="text-center py-20 border border-[#E2E8F0]">
                <Music className="w-16 h-16 text-[#1E3A8A] mx-auto mb-4" />
                <p className="font-heading text-xl text-[#64748B] mb-2">
                  {searchQuery ? 'No tracks found' : 'Your library is empty'}
                </p>
                <p className="font-body text-[#94A3B8] text-sm max-w-md mx-auto">
                  {searchQuery 
                    ? 'Try adjusting your search or filters' 
                    : 'Upload your MP3 files to get started. CRATELAB will analyze BPM, key, energy and structure automatically.'}
                </p>
                <Link
                  to="/upload"
                  className="inline-flex items-center gap-2 mt-6 px-4 py-2 bg-[#2563FF] hover:bg-[#1D4ED8] text-white font-heading text-sm transition-colors"
                >
                  <Plus className="w-4 h-4" />
                  Upload Tracks
                </Link>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Mobile Sidebar Overlay */}
      <AnimatePresence>
        {showMobileSidebar && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setShowMobileSidebar(false)}
              className="fixed inset-0 bg-white/60 backdrop-blur-sm z-40 lg:hidden"
            />
            
            {/* Sidebar Panel */}
            <motion.div
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ type: 'spring', damping: 25, stiffness: 300 }}
              className="fixed top-0 right-0 bottom-0 w-80 bg-white border-l border-[#E2E8F0] z-50 lg:hidden overflow-y-auto"
            >
              {/* Sidebar Header */}
              <div className="flex items-center justify-between p-4 border-b border-[#E2E8F0]">
                <h2 className="font-heading text-lg text-[#0F172A]">Collections</h2>
                <button
                  onClick={() => setShowMobileSidebar(false)}
                  className="p-2 text-[#64748B] hover:text-[#0F172A]"
                  data-testid="close-mobile-sidebar"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              {/* Crates Section */}
              <div className="p-4 border-b border-[#E2E8F0]">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-heading text-sm text-[#64748B] uppercase tracking-wider">
                    Crates
                    {isDragging && <span className="ml-2 text-[10px] text-[#2563FF] animate-pulse">Drop here</span>}
                  </h3>
                  <Link 
                    to="/crates" 
                    onClick={() => setShowMobileSidebar(false)}
                    className="text-[#2563FF] hover:text-[#3B82F6]"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </Link>
                </div>
                
                {/* All Tracks option */}
                <button
                  onClick={() => { setSelectedCrate('all'); setShowMobileSidebar(false); }}
                  className={`w-full p-3 mb-2 border transition-all text-left ${
                    selectedCrate === 'all'
                      ? 'border-[#2563FF] bg-[#EFF6FF]'
                      : 'border-[#E2E8F0] hover:border-[#2563FF]/50'
                  }`}
                  data-testid="mobile-all-tracks-btn"
                >
                  <div className="flex items-center gap-2">
                    <Music className="w-4 h-4 text-[#2563FF]" />
                    <span className="font-body text-sm text-[#0F172A] flex-1">All Tracks</span>
                    <span className="font-mono text-xs text-[#64748B]">{tracks.length}</span>
                  </div>
                </button>
                
                <div className="space-y-2">
                  {crates.map(crate => (
                    <div
                      key={crate.id}
                      onClick={() => { setSelectedCrate(crate.id); setShowMobileSidebar(false); }}
                      onDragOver={(e) => { e.preventDefault(); setDragOverCrate(crate.id); }}
                      onDragLeave={() => setDragOverCrate(null)}
                      onDrop={(e) => { handleDropOnCrate(crate.id, e); setShowMobileSidebar(false); }}
                      className={`p-3 border transition-all cursor-pointer ${
                        dragOverCrate === crate.id 
                          ? 'border-[#2563FF] bg-[#EFF6FF] scale-[1.02] ring-2 ring-[#22D3EE]/40 shadow-[0_8px_24px_rgba(37,99,255,0.18)]' 
                          : selectedCrate === crate.id
                            ? 'border-[#2563FF] bg-[#EFF6FF]'
                            : 'border-[#E2E8F0] hover:border-[#2563FF]/50'
                      }`}
                      data-testid={`mobile-crate-${crate.id}`}
                    >
                      <div className="flex items-center gap-2">
                        <div 
                          className="w-4 h-4 rounded-md flex-shrink-0"
                          style={{ backgroundColor: crate.color }}
                        />
                        <span className="font-body text-sm text-[#0F172A] truncate flex-1">{crate.name}</span>
                        <span className="font-mono text-xs text-[#64748B]">{crate.active_track_count ?? (crate.track_ids?.length || 0)}</span>
                      </div>
                    </div>
                  ))}
                </div>
                <Link 
                  to="/crates"
                  onClick={() => setShowMobileSidebar(false)}
                  className="mt-4 block text-center p-2 rounded-lg border border-dashed border-[#E2E8F0] text-[#64748B] hover:text-[#2563FF] hover:border-[#2563FF]/50 transition-colors font-mono text-xs"
                >
                  + New Crate
                </Link>
              </div>

              {/* Playlists Section - Always visible */}
              <div className="p-4">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-heading text-sm text-[#64748B] uppercase tracking-wider">
                    Playlists
                    {isDragging && <span className="ml-2 text-[10px] text-[#2563FF] animate-pulse">Drop here</span>}
                  </h3>
                  <Link 
                    to="/playlists" 
                    onClick={() => setShowMobileSidebar(false)}
                    className="text-[#2563FF] hover:text-[#3B82F6]"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </Link>
                </div>
                <p className="text-xs text-[#64748B] mb-3">Drag tracks here to add</p>
                <div className="space-y-2">
                  {playlists.length > 0 ? (
                    playlists.map(playlist => (
                      <div
                        key={playlist.id}
                        onDragOver={(e) => { e.preventDefault(); setDragOverPlaylist(playlist.id); }}
                        onDragLeave={() => setDragOverPlaylist(null)}
                        onDrop={(e) => { handleDropOnPlaylist(playlist.id, e); setShowMobileSidebar(false); }}
                        className={`p-3 rounded-xl border transition-all cursor-pointer ${
                          dragOverPlaylist === playlist.id 
                            ? 'border-[#2563FF] bg-[#EFF6FF] scale-[1.02] ring-2 ring-[#22D3EE]/40 shadow-[0_8px_24px_rgba(37,99,255,0.18)]' 
                            : 'border-[#E2E8F0] bg-white hover:border-[#2563FF]/50 hover:bg-[#F8FAFC]'
                        }`}
                        data-testid={`mobile-playlist-${playlist.id}`}
                      >
                        <Link 
                          to={`/playlist/${playlist.id}`}
                          onClick={() => setShowMobileSidebar(false)}
                          className="flex items-center gap-2"
                        >
                          <ListMusic className="w-4 h-4 text-[#2563FF]" />
                          <span className="font-body text-sm text-[#0F172A] truncate flex-1">{playlist.name}</span>
                          <span className="font-mono text-xs text-[#64748B]">{playlist.active_track_count ?? (playlist.track_ids?.length || 0)}</span>
                        </Link>
                      </div>
                    ))
                  ) : (
                    <p className="text-[#94A3B8] text-xs font-body py-2">No playlists yet</p>
                  )}
                </div>
                <Link 
                  to="/playlists"
                  onClick={() => setShowMobileSidebar(false)}
                  className="mt-4 block text-center p-2 rounded-lg border border-dashed border-[#E2E8F0] text-[#64748B] hover:text-[#2563FF] hover:border-[#2563FF]/50 transition-colors font-mono text-xs"
                  data-testid="mobile-new-playlist-link"
                >
                  + New Playlist
                </Link>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Audio Player is now mounted globally in App.js via GlobalAudioPlayer */}
    </div>
  );
}
